import { useMemo } from 'react'

import { CostCentersOptionsDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  AllocationOption,
  AllocationSelectOption,
  AssignmentsOptions,
  LINKABLE_RESOURCES,
} from 'modules/cost_centers/lib/types'

const getAssignmentsOptions = (
  optionFromResource: AllocationOption,
  assignmentsOptions: AssignmentsOptions
) => {
  return assignmentsOptions?.[optionFromResource.resourceType]?.nodes
}

export const useCostCenterOptions = (
  resourceDate: string,
  resourceId?: number,
  resourceType?: string,
  optionsFromResource?: AllocationOption[]
): { options: AllocationSelectOption[]; loading: boolean } => {
  const employeeId = optionsFromResource?.find(
    (option) => option.resourceType === LINKABLE_RESOURCES.Employee
  )?.resourceId

  const { data, loading } = useQuery(CostCentersOptionsDocument, {
    variables: {
      applyingOn: resourceDate,
      employeeId: employeeId,
      includeEmployeeAssignments: !!employeeId,
    },
  })

  const memoizedOptions = useMemo(() => {
    const options: AllocationSelectOption[] = []

    if (loading) return []

    const assignmentsOptions: AssignmentsOptions = data?.costCenters ?? {
      Employee: { nodes: [] },
    }

    if (optionsFromResource?.length) {
      optionsFromResource.forEach((option) => {
        const hasResourceAssignmentsOptions = getAssignmentsOptions(option, assignmentsOptions)

        if (hasResourceAssignmentsOptions && hasResourceAssignmentsOptions.length === 0) {
          return
        }

        options.push({
          label: option.label,
          value: option.resourceType,
          resourceId: option.resourceId,
        })
      })
    }

    if (resourceType) {
      options.push({
        label: i18n.t('cost_centers.select_options.custom'),
        value: resourceType,
        resourceId: resourceId,
      })
    }

    return options
  }, [resourceType, resourceId, optionsFromResource, loading, data?.costCenters])

  return { options: memoizedOptions, loading }
}
